import { useContext } from 'react'
import useMediaSelectionActions from '../actions/useMediaSelectionActions'
import { NewDevicePromptData } from '../services/useTriggerPromptsOnMediaSourcesUpdate'
import { UserContext, UserContextType } from '../UserContext'
import { AppContext, AppContextType } from '../AppContext'
import { AccentBadge } from '@/components/AccentBadge'
import { Button } from '@/UIComponents2/Button'
import SpecialMicrophoneIcon from '@/CustomIcons/SpecialMicrophoneIcon'
import SpecialSpeakerIcon from '@/CustomIcons/SpecialSpeakerIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from './components/DialogActionButtonsSection'
import { Trans, useTranslation } from 'react-i18next'
import CameraIcon from '@/CustomIcons/CameraIcon'
import { SettingsTab } from '@/components/SessionSettings/SessionSettings'
import { sessionSettingsTrigger } from '@/pages/SessionPage/SessionPage'

export default function NewMediaDevicePrompt({
    newDevicePromptData,
    onDefaultClose,
}: {
    newDevicePromptData: NewDevicePromptData
    onDefaultClose: () => void
}) {
    const { t } = useTranslation()
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { addAudioDeviceSelection } = useMediaSelectionActions()

    const onUseDevice = () => {
        const { deviceId, deviceType } = newDevicePromptData

        if (deviceType === 'audioInput') {
            const audioInputSource = state.mediaSources.audioInputs.filter((a) => a.deviceId === deviceId)[0] || null
            if (audioInputSource) {
                addAudioDeviceSelection(audioInputSource)
            }
        } else if (deviceType === 'audioOutput') {
            dispatch({
                type: 'update_media_selection',
                value: { audioOutput: deviceId },
            })
        } else if (deviceType === 'video') {
            dispatch({
                type: 'update_media_selection',
                value: { video: deviceId },
            })
        }

        onDefaultClose()
    }

    const handleOpenSessionSettings = (settingsTab: SettingsTab) => {
        onDefaultClose()
        sessionSettingsTrigger.trigger({ tab: settingsTab })
    }

    const generateDialogTitle = (newDevicePromptData: NewDevicePromptData) => {
        const { deviceType } = newDevicePromptData
        if (deviceType === 'audioInput') {
            return t(`New Audio Input Device Detected`)
        } else if (deviceType === 'audioOutput') {
            return t(`New Audio Output Device Detected`)
        } else if (deviceType === 'video') {
            return t(`New Camera Device Detected`)
        } else {
            return t(`New device detected`)
        }
    }

    const generateDialogParagraph = (newDevicePromptData: NewDevicePromptData) => {
        const { deviceType } = newDevicePromptData
        if (deviceType === 'audioInput') {
            return (
                <Trans
                    i18nKey="Do you want to share audio from this device? You can manage audio inputs in <0>Audio Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('audio')} />]}
                />
            )
        }
        if (deviceType === 'audioOutput') {
            return (
                <Trans
                    i18nKey="Do you want to use this device as your speakers? You can change the audio device later in <0>Audio Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('audio')} />]}
                />
            )
        }
        if (deviceType === 'video') {
            return (
                <Trans
                    i18nKey="Do you want to use this device as your camera? You can change the video device later in <0>Video Settings</0>"
                    t={t}
                    components={[<Button variant="link" onClick={() => handleOpenSessionSettings('video')} />]}
                />
            )
        }

        return null
    }

    const generateRightButtonText = (newDevicePromptData: NewDevicePromptData) => {
        const { deviceType } = newDevicePromptData
        if (deviceType === 'audioInput') {
            return t(`Use input`)
        } else if (deviceType === 'audioOutput') {
            return t(`Use as output`)
        } else if (deviceType === 'video') {
            return t(`Use camera`)
        } else {
            return t(`Use`)
        }
    }

    const icon = (deviceType: 'audioInput' | 'audioOutput' | 'video') => {
        if (deviceType === 'audioInput') {
            return <SpecialMicrophoneIcon />
        } else if (deviceType === 'audioOutput') {
            return <SpecialSpeakerIcon />
        } else if (deviceType === 'video') {
            return <CameraIcon className="text-muse-yellow" style={{ fontSize: '30px' }} />
        }
    }

    return (
        <div className="flex flex-col w-full">
            <DialogBody className="flex flex-col gap-6">
                <AccentBadge variant="highlight">{icon(newDevicePromptData.deviceType)}</AccentBadge>
                <DialogTitle className="pb-1">{generateDialogTitle(newDevicePromptData)}</DialogTitle>
                <div className="w-full text-center text-muse-yellow p-[8px] bg-white/5 rounded-2xl shadow-glow border-muse-toplit-low">
                    {newDevicePromptData.deviceLabel}
                </div>
                <div className="t-base-light">{generateDialogParagraph(newDevicePromptData)}</div>
                <DontShowAgain newDevicePromptData={newDevicePromptData} />
            </DialogBody>

            <DialogActionButtonsSection
                leftButtonText={t("Don't use")}
                onLeftButtonClicked={onDefaultClose}
                rightButtonText={generateRightButtonText(newDevicePromptData)}
                onRightButtonClicked={onUseDevice}
            />
        </div>
    )
}

function DontShowAgain({ newDevicePromptData }: { newDevicePromptData: NewDevicePromptData }) {
    const { t } = useTranslation()
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const isSelected = !!appState.appSettings.newDevicePromptSuppressedLabels[newDevicePromptData.deviceLabel]

    const onToggle = () => {
        const next = { ...appState.appSettings.newDevicePromptSuppressedLabels }

        if (isSelected) {
            delete next[newDevicePromptData.deviceLabel]
        } else {
            next[newDevicePromptData.deviceLabel] = true
        }

        appDispatch({
            type: 'update_app_settings',
            value: { newDevicePromptSuppressedLabels: next },
        })
    }

    return (
        <div className="flex gap-2 mt-4 mb-2">
            <div className={'flex items-center gap-2'}>
                <Checkbox checked={isSelected} onChange={onToggle} label={t("Don't show again for this device")} />
            </div>
        </div>
    )
}
